<script setup lang="ts">
  /**
   * Portal Layout
   *
   * Layout for client-facing portal (portal.claimshieldpro.com).
   * Features:
   * - Premium dark purple/gold theme
   * - Collapsible sidebar navigation
   * - Header with user menu
   * - Responsive design
   */
  import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { useAuthStore } from '../../stores/auth'
  import { isPortalIdentityMode } from '../../services/auth-mode'

  // App-level nav gating. The layout is shared, but feature flags are
  // per-app (e.g. portal's DATAQ_CHALLENGES_ENABLED) — the app passes the
  // routes to hide via router-level props instead of the shared layout
  // importing app config. Default hides nothing, so no other consumer moves.
  // First user: the Challenges item, which bbe0e10d8 gated at the routes and
  // buttons but left dangling in this sidebar (a click silently bounced to
  // the dashboard — Dugger's 08-25 "why did Challenge stop working" report).
  const props = withDefaults(defineProps<{ hiddenNavRoutes?: string[] }>(), {
    hiddenNavRoutes: () => [],
  })
  import { useUIStore } from '../../stores/ui'
  import { useClientMessagesStore } from '../../stores/clientMessages'
  import { api } from '../../services/api'

  const router = useRouter()
  const route = useRoute()
  const authStore = useAuthStore()
  const uiStore = useUIStore()

  // ── Sidebar identity strip ──────────────────────────────────────────────
  // The carrier's own logo, resolved for both a real client user and a staff
  // member viewing-as. Design + the aspect-ratio reasoning: sketch 087.
  const logoBroken = ref(false)

  // `clientLogoUrl` is API-relative (`/api/v1/public/logos/...`) and the portal
  // is served from a different origin, so the API base goes in front of it —
  // the same resolution ApplicationBrandingView does.
  const clientLogoSrc = computed(() => {
    const path = authStore.clientLogoUrl
    if (logoBroken.value || !path) return null
    const base = (api.defaults.baseURL || '').replace(/\/api$/, '')
    return `${base}${path}`
  })

  const clientDisplayName = computed(
    () => authStore.viewAsClientName || authStore.clientName || 'ClaimShield',
  )

  // Staff view-as switches the carrier underneath this component, and for that
  // path the URL is derived without knowing whether a logo exists — so a 404 is
  // expected and routine. Clear the failure flag on every switch, or the first
  // logo-less carrier would suppress the mark for every carrier picked after.
  watch(() => authStore.clientLogoUrl, () => { logoBroken.value = false })

  // ── Messages + portal presence (client support, Phase 2B) ──
  // The shell owns both because both are shell-lifetime concerns: the header
  // badge must be current on every page, and the presence heartbeat must run
  // for as long as a portal tab is open and stop the moment it is not.
  //
  // Gated on the REAL token's role, not `isClient` (which reads through
  // view-as): a staff member viewing a client's portal is not that client
  // sitting at their desk, and marking them present would put a green dot on
  // a carrier who is not there. The server agrees — `get_client_for_user`
  // 403s a staff token on this surface.
  const clientMessages = useClientMessagesStore()
  const isRealClient = computed(() => authStore.user?.role === 'client')
  const messagesUnread = computed(() => clientMessages.unreadCount)

  // Demo-mode guided tour launcher. This layout lives in @claimshield/shared, so
  // it stays decoupled from the portal-only DemoTour component: the button fires
  // a window event that the portal app root listens for. Harmless where nothing
  // listens (e.g. ops). Plan: knowledge/portal/plans/2026-07-16-portal-demo-mode.md
  function startDemoTour(): void {
    window.dispatchEvent(new CustomEvent('cs:demo-tour-start'))
  }

  // ── Staff portal "view-as": pick a client to scope every tab to (read-only) ──
  // Staff/admin only. The selection lives in the auth store; the api request
  // interceptor reads it to append ?client_id= on portal reads. Clients never
  // see this (the bar + gate are isStaff-guarded).
  interface ClientPick { id: string; name: string; status?: string | null }
  const viewAsClients = ref<ClientPick[]>([])
  const viewAsLoading = ref(false)

  // Client-status filter — lives INSIDE the picker dropdown (the PSelect #header
  // slot), matching the ops dashboard: All / Active / Lost. 'all' = no filter.
  const statusFilter = ref<string>('all')
  const CLIENT_STATUS_CHIPS = [
    { label: 'All', value: 'all' },
    { label: 'Active', value: 'Active' },
    { label: 'Lost', value: 'Lost' },
  ]
  // The list the PSelect shows. (c.status || 'Active') mirrors ops — a blank
  // status counts as Active. The currently-viewed client always stays in the
  // list so the control never loses its value when a filter hides it.
  const filteredViewAsClients = computed<ClientPick[]>(() => {
    if (statusFilter.value === 'all') return viewAsClients.value
    return viewAsClients.value.filter(
      (c) => (c.status || 'Active') === statusFilter.value || c.id === authStore.viewAsClientId,
    )
  })

  async function loadViewAsClients() {
    if (!authStore.isStaff) return
    viewAsLoading.value = true
    try {
      const res = await api.get<{ status: string; data: { clients: ClientPick[] } | null }>(
        '/v1/client/dashboard/clients',
      )
      if (res.data.status === 'ok' && res.data.data) {
        viewAsClients.value = res.data.data.clients
      }
    } catch {
      // picker just stays empty
    } finally {
      viewAsLoading.value = false
    }
  }

  const viewAsSelection = computed<string | null>({
    get: () => authStore.viewAsClientId,
    set: (id) => {
      const name = id ? (viewAsClients.value.find((c) => c.id === id)?.name ?? null) : null
      authStore.setViewAsClient(id, name)
    },
  })

  // ── Assigned consultant — persistent contact affordance in the sidebar ──
  // UX-audit #5: "contact your consultant" was a dead-end on every page but
  // Services. Reuses the SAME client-scoped overview endpoint Services/Settings
  // already call (no new endpoint); the mailto is display-only, not a wired
  // action, so it stays inside the v1 scope lock. Fetched once (the layout
  // mounts once per session). Client-only + silent failure → the chip just hides.
  const consultantName = ref<string | null>(null)
  const consultantEmail = ref<string | null>(null)
  const consultantFirstName = computed(
    () => consultantName.value?.trim().split(/\s+/)[0] || null,
  )

  async function loadConsultant() {
    // Clients always; staff only once they're viewing a client (the api
    // interceptor scopes the call with client_id, so view-as mirrors what
    // that client's users see).
    if (!authStore.isClient && !authStore.isViewingAsClient) {
      consultantName.value = null
      consultantEmail.value = null
      return
    }
    consultantName.value = null
    consultantEmail.value = null
    try {
      const res = await api.get<{
        status: string
        data: { consultant_name: string | null; consultant_email: string | null } | null
      }>('/v1/client/dashboard/overview')
      if (res.data.status === 'ok' && res.data.data) {
        consultantName.value = res.data.data.consultant_name?.trim() || null
        consultantEmail.value = res.data.data.consultant_email?.trim() || null
      }
    } catch {
      // chip just hides
    }
  }

  // Staff view-as: refetch when the viewed client changes (mount-only fetch
  // would pin the first client's consultant for the whole session).
  watch(() => authStore.viewAsClientId, () => loadConsultant())

  // ── The persistent "?" help menu (client support, Phase 3A) ──
  // Spec §4b: the first-login tour is "dismissable; replayable from a persistent
  // '?' help menu in the portal header", and that menu also carries the
  // quickstart guide (§4c) and the way to reach a human (§5).
  //
  // Same decoupling as `startDemoTour` above: this layout lives in
  // @claimshield/shared, so Replay fires a window event that the portal app root
  // listens for. Harmless anywhere nothing listens.
  //
  // The quickstart is a static file in the PORTAL's own `public/guides/`, so the
  // link a client follows stays on portal.claimshieldpro.com — the consultant-
  // facing library in ops carries the same guide as a client-facing entry, and
  // both point at this one file. Relative on purpose: it resolves in dev too.
  const QUICKSTART_GUIDE_URL = '/guides/your-claimshield-portal.html'

  const helpMenu = ref<{ toggle: (e: Event) => void } | null>(null)

  function toggleHelpMenu(event: Event): void {
    helpMenu.value?.toggle(event)
  }

  function replayLaunchTour(): void {
    window.dispatchEvent(new CustomEvent('cs:launch-tour-start'))
  }

  // A computed so the consultant's name lands in the labels once
  // `loadConsultant()` answers, and the rows that need a value disappear when we
  // do not have one rather than rendering a dead `mailto:`.
  const helpMenuItems = computed(() => {
    const items: Record<string, unknown>[] = [
      {
        label: 'Replay the portal tour',
        icon: 'pi pi-play-circle',
        command: () => replayLaunchTour(),
      },
      {
        label: 'Quickstart guide',
        icon: 'pi pi-book',
        url: QUICKSTART_GUIDE_URL,
        target: '_blank',
      },
    ]
    const who = consultantFirstName.value
    if (isRealClient.value) {
      items.push({ separator: true })
      items.push({
        label: who ? `Message ${who}` : 'Message your consultant',
        icon: 'pi pi-comments',
        command: () => router.push('/messages'),
      })
    }
    if (consultantEmail.value) {
      if (!isRealClient.value) items.push({ separator: true })
      items.push({
        label: who ? `Email ${who}` : 'Email your consultant',
        icon: 'pi pi-envelope',
        url: `mailto:${consultantEmail.value}`,
      })
    }
    return items
  })

  // ── Staff view-as: seat-review-pending banner (spec 2026-08-03) ──
  // A CLIENT with an open seat-review requirement is blocked into the portal's
  // own /seat-review route (portal router guard) — but staff view-as is never
  // gated, so staff would otherwise see a normal dashboard with no hint that
  // the client themselves is locked out. This banner surfaces that state.
  // Read-only + best-effort: any failure (404 flag off, network) just hides it.
  const seatReviewPending = ref<{ active: number; seats: number } | null>(null)
  async function loadSeatReviewBanner() {
    seatReviewPending.value = null
    if (!authStore.isStaff || !authStore.viewAsClientId) return
    try {
      const res = await api.get<{
        status: string
        data: { gated: boolean; active_count?: number; seats?: number } | null
      }>('/v1/client/seat-review', { params: { client_id: authStore.viewAsClientId } })
      if (res.data.data?.gated) {
        seatReviewPending.value = {
          active: res.data.data.active_count ?? 0,
          seats: res.data.data.seats ?? 0,
        }
      }
    } catch {
      // banner just hides
    }
  }
  watch(() => authStore.viewAsClientId, () => loadSeatReviewBanner())
  // Staff can now resolve the review on the client's behalf (TRACK #12), so
  // re-read on leaving that screen — otherwise the banner outlives the fix.
  watch(
    () => route.name,
    (to, from) => {
      if (from === 'seat-review' && to !== 'seat-review') loadSeatReviewBanner()
    },
  )

  // Client portal is LIGHT-ONLY (Ahmed, 2026-07-16 — dark theme deferred, needs a
  // full overhaul). Lock light on mount: this clears any `dark-mode` a returning
  // user had persisted (PrimeVue's darkModeSelector) and we no longer add the
  // legacy `body.portal-dark` overlay. localStorage is per-origin, so this does
  // not affect the ops app. Plan: knowledge/portal/plans/2026-07-16-portal-ux-core-fixes.md
  onMounted(() => {
    uiStore.setTheme('light')
    loadViewAsClients()
    loadConsultant()
    loadSeatReviewBanner()
    // Starts the presence heartbeat AND the unread poll — one request answers
    // both (the heartbeat response carries the badge count).
    clientMessages.startHeartbeat(isRealClient.value)
  })

  // Logging out or leaving the portal shell ends presence. Without this the
  // interval outlives the layout and a closed session keeps reporting a person
  // who has gone.
  onUnmounted(() => {
    clientMessages.stopHeartbeat()
  })

  // Navigation — accordion-tree structure (sketch 066 variant A, Ahmed 2026-07-31):
  // a standalone Dashboard row, then accordion parent groups whose children
  // hang off a cs-tree connector. Tabler icons per sketch 065. The collapsed
  // (w-16) sidebar flattens to leaf icons, as before.
  const dashboardItem = { label: 'Dashboard', icon: 'ti ti-home', route: '/' }
  const navGroups = [
    {
      title: 'Fleet & Compliance',
      icon: 'ti ti-steering-wheel',
      items: [
        { label: 'Safety Scores', icon: 'ti ti-chart-line', route: '/safety-scores' },
        { label: 'Accidents', icon: 'ti ti-car-crash', route: '/accidents' },
        { label: 'Roadside Inspections', icon: 'ti ti-shield-check', route: '/roadside-inspections' },
        { label: 'Challenges', icon: 'ti ti-flag', route: '/challenges' },
        { label: 'Drivers', icon: 'ti ti-users', route: '/drivers' },
        { label: 'Vehicles', icon: 'ti ti-truck', route: '/vehicles' },
        { label: 'Dispatch', icon: 'ti ti-truck-delivery', route: '/dispatch' },
        { label: 'Repairs', icon: 'ti ti-tools', route: '/repairs' },
        { label: 'Invoice Review', icon: 'ti ti-receipt', route: '/invoice-review' },
        { label: 'Audits', icon: 'ti ti-clipboard-check', route: '/audits' },
        { label: 'Training', icon: 'ti ti-school', route: '/training' },
        { label: 'Handbook', icon: 'ti ti-book', route: '/handbook' },
      ],
    },
    {
      title: 'Account',
      icon: 'ti ti-user-circle',
      items: [
        { label: 'Billing', icon: 'ti ti-credit-card', route: '/billing' },
        { label: 'Documents', icon: 'ti ti-folder', route: '/documents' },
        { label: 'Notifications', icon: 'ti ti-bell', route: '/contacts' },
      ],
    },
  ]

  // Accordion state — both groups open by default (first groups open by default;
  // we have exactly two). Stored as the CLOSED set so new groups default open.
  const closedGroups = ref<Set<string>>(new Set())
  function toggleGroup(title: string) {
    const next = new Set(closedGroups.value)
    if (next.has(title)) next.delete(title)
    else next.add(title)
    closedGroups.value = next
  }
  const groupHasActive = (group: { items: { route: string }[] }) =>
    group.items.some((i) => isNavActive(i.route))
  // What the sidebar renders — the full list minus app-hidden routes. A group
  // whose items all hide disappears with them. The collapsed rail flattens
  // this SAME filtered list: until 2026-09-08 it iterated the unfiltered one,
  // so a route hidden from the expanded sidebar came straight back as an icon
  // at 64px. (Page titles never came from the nav list — the chrome bar reads
  // `route.meta.title` — so nothing else needs the full list.)
  const visibleNavGroups = computed(() =>
    navGroups
      .map((g) => ({
        ...g,
        items: g.items.filter((i) => !props.hiddenNavRoutes.includes(i.route)),
      }))
      .filter((g) => g.items.length > 0),
  )
  const visibleLeaves = computed(() => [
    dashboardItem,
    ...visibleNavGroups.value.flatMap((g) => g.items),
  ])

  // User menu items
  const userMenuItems = [
    { label: 'Settings', icon: 'pi pi-cog', command: () => router.push('/settings') },
    { separator: true },
    { label: 'Logout', icon: 'pi pi-sign-out', command: () => handleLogout() },
  ]

  const isNavActive = (itemRoute: string) => {
    if (itemRoute === '/') return route.path === '/'
    return route.path.startsWith(itemRoute)
  }

  async function handleLogout() {
    // Identity sign-in: the /logout view ends the portal session AND the
    // sign-in server's session, then lands on /signed-out. Going straight to
    // /login left the sign-in server remembering the person, so the next
    // "sign in" walked back in with no password (Ahmed's walk, 2026-10-22).
    if (isPortalIdentityMode() && !authStore.staffViewAsContext) {
      await router.push({ name: 'logout' })
      return
    }
    await authStore.logout()
    router.push('/login')
  }
</script>

<template>
  <div class="flex h-screen portal-shell-bg">
    <!-- ClaimShield signature line — ops parity (sketch 064 variant A, Ahmed
         2026-07-29): a single 2px purple→gold hairline at the base of the
         64px chrome band, running the full viewport width so it reads as one
         continuous stroke from the sidebar brand block through the white
         title bar. Desktop only; mobile keeps its own bordered header. -->
    <div
      class="pointer-events-none fixed inset-x-0 top-16 z-40 hidden h-0.5 bg-gradient-to-r from-primary-800 via-primary-600 to-accent-500 lg:block"
    />
    <!-- Sidebar -->
    <aside
      :class="[
        'portal-sidebar fixed inset-y-0 left-0 z-30 flex flex-col transition-all duration-300',
        uiStore.sidebarCollapsed ? 'w-16' : 'w-64',
        uiStore.sidebarMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
      ]"
    >
      <!-- Identity strip — the carrier's own mark. Dugger gave this slot the
           carrier's NAME in 1f6b26d05 (2026-01-28) and left it our shield; this
           finishes it. Layout per sketch 087 (B, with C's label). -->
      <!-- Collapsed, px-4 leaves a 32px content box for a 40px mark, so it
           spills past the 64px rail. Centre it instead — the mark is the only
           thing in the strip at that width. -->
      <div
        class="portal-sidebar-header flex h-16 items-center gap-3"
        :class="uiStore.sidebarCollapsed ? 'justify-center px-2' : 'px-4'"
      >
        <!-- No logo, or it failed to load: the shield, exactly as shipped. -->
        <div
          v-if="!clientLogoSrc"
          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-primary-700 to-accent-500"
        >
          <svg class="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
          </svg>
        </div>

        <!-- Collapsed to the 64px rail: only a square fits, so the lockup
             gives way to the contained tile. -->
        <div v-else-if="uiStore.sidebarCollapsed" class="cs-mark-tile">
          <img :src="clientLogoSrc" alt="" @error="logoBroken = true">
        </div>

        <!-- Expanded, with a logo: the mark runs the width of the strip. A
             carrier logo is almost always a wide wordmark, and a wordmark
             inside the 40px square is an unreadable smudge (sketch 087). -->
        <div v-else class="min-w-0 flex-1" :title="clientDisplayName">
          <img
            class="cs-mark-lockup"
            :src="clientLogoSrc"
            :alt="clientDisplayName"
            @error="logoBroken = true"
          >
          <!-- The company name is deliberately NOT repeated here. The mark above
               already says who they are, and squeezing both into 224px truncated
               it to "ASHLINE TRANSPORT…" — worse than absent. What the mark
               cannot say is whose product this is, so that is what the line
               carries. Full name stays reachable via the title attribute. -->
          <div class="cs-mark-sub">
            <span class="cs-mark-portal"><b>ClaimShield</b> Portal</span>
          </div>
        </div>

        <!-- No logo, expanded: the name sits beside the shield, as before. -->
        <div v-if="!clientLogoSrc && !uiStore.sidebarCollapsed" class="min-w-0">
          <span class="block truncate text-[15px] font-extrabold text-secondary-900">
            {{ clientDisplayName }}
          </span>
          <span class="cs-mark-portal"><b>ClaimShield</b> Portal</span>
        </div>
      </div>

      <!-- Navigation — accordion tree (sketch 066 variant A):
           standalone Dashboard, then parent rows with cs-tree children.
           Active treatment differs by level: top-level = lavender pill,
           child = purple text only, parent = purple text when its group
           holds the active page. -->
      <nav class="flex-1 overflow-y-auto px-3 py-3">
        <template v-if="!uiStore.sidebarCollapsed">
          <RouterLink
            :to="dashboardItem.route"
            :class="['pn-row', isNavActive(dashboardItem.route) ? 'pn-row-active' : '']"
            @click="uiStore.closeMobileSidebar()"
          >
            <i :class="[dashboardItem.icon, 'pn-ic']" />
            <span>{{ dashboardItem.label }}</span>
          </RouterLink>

          <div v-for="group in visibleNavGroups" :key="group.title" class="mt-1">
            <button
              type="button"
              :class="['pn-row pn-parent', groupHasActive(group) ? 'pn-parent-active' : '']"
              @click="toggleGroup(group.title)"
            >
              <i :class="[group.icon, 'pn-ic']" />
              <span>{{ group.title }}</span>
              <i :class="['ti ti-chevron-up pn-chev', closedGroups.has(group.title) ? 'pn-chev-closed' : '']" />
            </button>
            <div v-if="!closedGroups.has(group.title)" class="pn-kids cs-tree">
              <RouterLink
                v-for="item in group.items"
                :key="item.route"
                :to="item.route"
                :class="['pn-kid', isNavActive(item.route) ? 'pn-kid-active' : '']"
                @click="uiStore.closeMobileSidebar()"
              >
                <i :class="[item.icon, 'pn-kid-ic']" />
                <span class="truncate">{{ item.label }}</span>
              </RouterLink>
            </div>
          </div>
        </template>

        <!-- Collapsed rail — flat leaf icons, as before -->
        <div v-else class="space-y-0.5">
          <RouterLink
            v-for="item in visibleLeaves"
            :key="item.route"
            :to="item.route"
            :class="['pn-row justify-center !px-0', isNavActive(item.route) ? 'pn-row-active' : '']"
            v-tooltip.right="item.label"
            @click="uiStore.closeMobileSidebar()"
          >
            <i :class="[item.icon, 'pn-ic']" />
          </RouterLink>
        </div>
      </nav>

      <!-- Assigned consultant — persistent contact escape hatch (UX-audit #5) -->
      <div
        v-if="consultantName"
        :class="uiStore.sidebarCollapsed ? 'py-1.5' : 'consultant-card'"
      >
        <template v-if="!uiStore.sidebarCollapsed">
          <div class="consultant-eyebrow">Your consultant</div>
          <div class="consultant-name">{{ consultantName }}</div>
          <a
            v-if="consultantEmail"
            :href="`mailto:${consultantEmail}`"
            class="consultant-mail"
          >
            <i class="ti ti-mail" />
            <span>Email{{ consultantFirstName ? ` ${consultantFirstName}` : '' }}</span>
          </a>
        </template>
        <a
          v-else
          :href="consultantEmail ? `mailto:${consultantEmail}` : undefined"
          class="consultant-collapsed"
          v-tooltip.right="`Your consultant: ${consultantName}`"
        >
          <i class="ti ti-user" />
        </a>
      </div>

      <!-- Account card (ops parity) — the user menu (Settings / Logout) now lives
           in the sidebar, not the top-right header. -->
      <PMenu ref="userMenu" :model="userMenuItems" popup />
      <button
        type="button"
        :class="['sb-account self-stretch text-left', uiStore.sidebarCollapsed ? 'justify-center !px-1' : '']"
        v-tooltip.right="uiStore.sidebarCollapsed ? authStore.userDisplayName : undefined"
        @click="($refs.userMenu as any).toggle($event)"
      >
        <span class="sb-avatar">{{ authStore.userInitials }}</span>
        <div v-if="!uiStore.sidebarCollapsed" class="min-w-0 flex-1">
          <div class="truncate text-[13px] font-bold text-secondary-900">{{ authStore.userDisplayName }}</div>
          <div
            v-if="authStore.user?.email && authStore.user.email !== authStore.userDisplayName"
            class="truncate text-[11px] text-secondary-500"
          >{{ authStore.user.email }}</div>
        </div>
        <i v-if="!uiStore.sidebarCollapsed" class="pi pi-ellipsis-v text-xs text-secondary-400" />
      </button>

      <!-- Collapse toggle -->
      <button
        class="hidden h-12 w-full box-border items-center justify-center border-t border-secondary-200 text-secondary-400 transition-colors hover:bg-secondary-50 hover:text-secondary-700 lg:flex"
        type="button"
        :aria-label="uiStore.sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        :aria-expanded="!uiStore.sidebarCollapsed"
        @click="uiStore.toggleSidebar"
      >
        <i :class="uiStore.sidebarCollapsed ? 'ti ti-chevron-right' : 'ti ti-chevron-left'" />
      </button>
    </aside>

    <!-- Mobile sidebar overlay -->
    <div
      v-if="uiStore.sidebarMobileOpen"
      class="fixed inset-0 z-20 bg-black/70 backdrop-blur-sm lg:hidden"
      @click="uiStore.closeMobileSidebar"
    />

    <!-- Main content area -->
    <div
      :class="[
        'flex min-w-0 flex-1 flex-col transition-all duration-300',
        uiStore.sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64',
      ]"
    >
      <!-- Desktop chrome bar — ops parity (sketch 064 variant A, 2026-07-29).
           The page title lives HERE, exactly as in OpsLayout; PageHeader hides
           its <h1> on desktop so the single-title rule from 2026-07-21 still
           holds — inverted: chrome owns the title, views own subtitle/actions. -->
      <header
        class="sticky top-0 z-10 hidden h-16 flex-shrink-0 items-center bg-white px-6 lg:flex"
        data-tour-chrome
      >
        <h1 class="text-lg font-semibold text-secondary-900">
          {{ route.meta.title?.toString().split(' | ')[0] || 'Dashboard' }}
        </h1>
        <!-- Messages — the entry point to the one ongoing consultant thread.
             Sits before the mailto because it is the channel we want used:
             email answers land in one person's inbox, the thread is on the
             record for whoever covers. -->
        <RouterLink
          v-if="isRealClient"
          to="/messages"
          class="pl-msg ml-auto"
          :class="route.path.startsWith('/messages') ? 'pl-msg-active' : ''"
        >
          <i class="ti ti-message-circle text-base" />
          <span>Messages</span>
          <span v-if="messagesUnread > 0" class="pl-msg-badge">{{ messagesUnread }}</span>
        </RouterLink>
        <a
          v-if="consultantEmail"
          :href="`mailto:${consultantEmail}`"
          :class="[
            'text-sm font-medium text-primary-700 transition-colors hover:text-primary-600',
            isRealClient ? 'ml-4' : 'ml-auto',
          ]"
        >
          Need help? Email {{ consultantFirstName }}
        </a>
        <!-- The persistent "?" — tour replay, quickstart, and the way to a
             human. `ml-auto` only when nothing before it already claimed it. -->
        <button
          type="button"
          class="pl-help"
          :class="{ 'ml-auto': !isRealClient && !consultantEmail }"
          data-tour="help-menu"
          data-testid="portal-help-menu"
          aria-label="Help"
          aria-haspopup="true"
          v-tooltip.bottom="'Help — replay the tour, open the quickstart guide'"
          @click="toggleHelpMenu"
        >
          <i class="pi pi-question-circle text-base" />
        </button>
      </header>

      <!-- Mobile header — sidebar toggle + the Messages entry (the one action
           worth a permanent slot on a phone: it is how a carrier reaches us). -->
      <header
        class="portal-header sticky top-0 z-10 flex h-16 items-center border-b px-4 lg:hidden"
        data-tour-chrome
      >
        <button
          class="rounded-lg p-2 text-secondary-500 hover:bg-secondary-100 hover:text-secondary-900"
          @click="uiStore.toggleMobileSidebar"
        >
          <i class="pi pi-bars text-xl" />
        </button>
        <RouterLink
          v-if="isRealClient"
          to="/messages"
          class="pl-msg ml-auto"
          :class="route.path.startsWith('/messages') ? 'pl-msg-active' : ''"
          aria-label="Messages"
        >
          <i class="ti ti-message-circle text-base" />
          <span>Messages</span>
          <span v-if="messagesUnread > 0" class="pl-msg-badge">{{ messagesUnread }}</span>
        </RouterLink>
        <!-- No `data-tour` here on purpose: two matches would make the tour's
             spotlight depend on DOM order rather than on what is visible, and a
             missing anchor already degrades to a parked card. -->
        <button
          type="button"
          class="pl-help"
          :class="{ 'ml-auto': !isRealClient }"
          data-testid="portal-help-menu-mobile"
          aria-label="Help"
          aria-haspopup="true"
          @click="toggleHelpMenu"
        >
          <i class="pi pi-question-circle text-base" />
        </button>
      </header>

      <!-- One popup, both triggers: PMenu re-anchors to whichever button was
           clicked, and only one header is ever on screen at a given width. -->
      <PMenu ref="helpMenu" :model="helpMenuItems" :popup="true" />


      <!-- Staff portal view-as bar (staff/admin only — clients never see this) -->
      <div
        v-if="authStore.isStaff"
        class="view-as-bar flex flex-wrap items-center gap-2.5 border-b px-4 py-2.5 lg:px-6"
      >
        <i class="pi pi-eye text-accent-600" />
        <span class="text-sm font-medium text-secondary-700">Viewing client:</span>
        <PSelect
          v-model="viewAsSelection"
          :options="filteredViewAsClients"
          optionLabel="name"
          optionValue="id"
          filter
          :loading="viewAsLoading"
          placeholder="Pick a client to view"
          class="min-w-[16rem]"
        >
          <!-- Status filter INSIDE the dropdown (All / Active / Lost), like the
               ops dashboard. Buttons use the global .picker-status-* classes
               (explicit hex) so they survive body.portal-dark on the light,
               teleported overlay panel. -->
          <template #header>
            <div class="picker-status-bar flex items-center gap-1 px-3 py-2">
              <button
                v-for="opt in CLIENT_STATUS_CHIPS"
                :key="opt.value"
                type="button"
                class="picker-status-btn flex-1 rounded px-2 py-1 text-xs font-medium transition-colors"
                :class="{ 'is-active': statusFilter === opt.value }"
                @click.stop="statusFilter = opt.value"
              >
                {{ opt.label }}
              </button>
            </div>
          </template>
          <template #option="slotProps">
            <div class="flex items-center gap-2">
              <span>{{ slotProps.option.name }}</span>
              <PTag
                v-if="slotProps.option.status && slotProps.option.status !== 'Active'"
                :value="slotProps.option.status"
                :severity="slotProps.option.status === 'Lost' ? 'danger' : 'secondary'"
                class="ml-1 text-xs"
              />
            </div>
          </template>
        </PSelect>
        <button
          v-if="authStore.viewAsClientId"
          class="text-xs font-medium text-secondary-600 underline-offset-2 hover:text-secondary-900 hover:underline"
          @click="viewAsSelection = null"
        >
          Clear
        </button>
        <!-- Hidden while demo is on: reaching LIVE requires stopping the demo
             first, so a screen-share demo is never one mis-click from real writes. -->
        <button
          v-if="authStore.viewAsClientId && !authStore.demoMode"
          type="button"
          class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold transition-colors"
          :class="authStore.editOnBehalf
            ? 'bg-danger-100 text-danger-700 hover:bg-danger-200'
            : 'bg-accent-500/15 text-accent-600 hover:bg-accent-500/25'"
          data-testid="edit-mode-toggle"
          @click="authStore.setEditOnBehalf(!authStore.editOnBehalf)"
        >
          <i class="pi text-[10px]" :class="authStore.editOnBehalf ? 'pi-pencil' : 'pi-lock'" />
          {{ authStore.editOnBehalf ? 'Editing — click to stop' : 'Read-only — click to edit' }}
        </button>
        <button
          v-if="authStore.viewAsClientId"
          type="button"
          class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold transition-colors"
          :class="authStore.demoMode
            ? 'bg-primary-100 text-primary-700 hover:bg-primary-200'
            : 'bg-secondary-200 text-secondary-600 hover:bg-secondary-300'"
          data-testid="demo-mode-toggle"
          @click="authStore.setDemoMode(!authStore.demoMode)"
        >
          <i class="pi text-[10px]" :class="authStore.demoMode ? 'pi-stop-circle' : 'pi-play-circle'" />
          {{ authStore.demoMode ? 'Demo on — click to stop' : 'Demo' }}
        </button>
      </div>

      <!-- Edit-on-behalf live banner (staff/admin acting as a real client) -->
      <div
        v-if="authStore.editOnBehalf && authStore.viewAsClientId"
        class="edit-on-behalf-banner flex items-center gap-2 bg-danger-600 px-4 py-1.5 text-xs font-semibold text-white lg:px-6"
        data-testid="edit-on-behalf-banner"
      >
        <i class="pi pi-exclamation-triangle" />
        Acting as {{ authStore.viewAsClientName || 'this client' }} — actions here are LIVE (real emails / orders / files).
      </div>

      <!-- Demo-mode banner (staff demoing on a client — all actions simulated) -->
      <div
        v-if="authStore.demoMode && authStore.viewAsClientId"
        class="flex items-center gap-2 bg-primary-600 px-4 py-1.5 text-xs font-semibold text-white lg:px-6"
        data-testid="demo-mode-banner"
      >
        <i class="pi pi-play-circle" />
        Demo mode — actions are simulated. Nothing is saved, sent, or ordered. Turning demo off resets everything.
        <button
          type="button"
          class="ml-auto rounded-full bg-white/20 px-2.5 py-0.5 text-[11px] font-semibold hover:bg-white/30"
          data-testid="demo-tour-start"
          @click="startDemoTour"
        >
          Start guided tour
        </button>
      </div>

      <!-- Seat-review-pending banner (staff view-as only — spec 2026-08-03).
           The client is blocked into their own /seat-review route; this just
           lets staff know why, without gating the staff view itself. -->
      <div
        v-if="seatReviewPending"
        class="bg-amber-100 text-amber-900 text-sm px-4 py-2"
        data-testid="seat-review-banner"
      >
        Seat review pending: {{ seatReviewPending.active }} active drivers /
        {{ seatReviewPending.seats }} billed seats. The client resolves this at
        login, or you can walk them through it.
        <RouterLink
          v-if="route.name !== 'seat-review'"
          :to="{ name: 'seat-review' }"
          class="ml-2 font-semibold underline"
          data-testid="seat-review-banner-open"
        >
          Open the seat review
        </RouterLink>
      </div>

      <!-- Page content -->
      <main class="flex-1 overflow-y-auto">
        <div class="page-container">
          <!-- Staff with no client selected: gate the portal — every tab needs a
               client to scope to. Clients never hit this branch. -->
          <div
            v-if="authStore.isStaff && !authStore.viewAsClientId"
            class="mx-auto mt-10 max-w-md rounded-xl border border-dashed border-secondary-300 bg-white p-10 text-center shadow-sm"
            data-testid="view-as-gate"
          >
            <i class="pi pi-users mb-3 block text-4xl text-secondary-400" />
            <p class="text-secondary-700">Pick a client above to view their portal.</p>
          </div>
          <RouterView v-else :key="authStore.viewAsClientId || 'self'" />
        </div>

        <!-- Footer — legal links + support contact -->
        <footer class="portal-footer mt-8 border-t px-6 py-4 text-xs text-secondary-500">
          <div class="flex flex-col items-center justify-between gap-2 sm:flex-row">
            <p>
              Questions? Email
              <a
                href="mailto:support@claimshieldpro.com"
                class="text-accent-800 transition-colors hover:text-accent-700"
              >support@claimshieldpro.com</a>
            </p>
            <p>
              <a
                href="https://claimshieldpro.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                class="transition-colors hover:text-secondary-900"
              >Terms of Service</a>
              <span class="mx-1.5">&middot;</span>
              <a
                href="https://claimshieldpro.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                class="transition-colors hover:text-secondary-900"
              >Privacy Policy</a>
            </p>
          </div>
        </footer>
      </main>
    </div>
  </div>
</template>

<style scoped>
/* LIGHT app background — matches the ops shell (bg-secondary-50). The dark slate
   sidebar sits on top as the brand anchor. */
.portal-shell-bg {
  background: theme('colors.secondary.50');
}

/* LIGHT sidebar — refined-light pattern (sketch 066 variant A, Ahmed 2026-07-31).
   Replaces the dark slate treatment (2026-07-13). PORTAL-ONLY divergence from
   the ops dark sidebar, pending the same-core-look review with Dugger. */
.portal-sidebar {
  background: #fff;
  color: theme('colors.secondary.600');
  border-right: 1px solid theme('colors.secondary.200');
}

/* ── Identity strip: the carrier's mark (sketch 087) ──────────────────────
   Two shapes for one logo, because the strip has two widths. Expanded, the
   mark runs full-width capped by HEIGHT — carrier logos are overwhelmingly
   wide wordmarks and a wordmark scaled to fit a 40px square is unreadable.
   Collapsed, the rail is 64px and takes nothing but a square, so it reverts
   to the contained tile. */
/* Capped by HEIGHT so every aspect ratio gets the same optical weight: a wide
   wordmark runs out to the strip's width, a squarer mark stays 34px tall
   instead of being starved by a width cap it never reaches. 34 + 4 + 13 = 51px
   inside the 64px strip. */
.cs-mark-lockup {
  display: block;
  max-height: 34px;
  max-width: 100%;
  object-fit: contain;
  object-position: left center;
}

.cs-mark-sub {
  display: flex;
  align-items: baseline;
  margin-top: 4px;
  min-width: 0;
  white-space: nowrap;
}

/* Whose product this is. Without it a carrier's logo top-left reads as "this
   application is ours", which it is not. */
.cs-mark-portal {
  flex: none;
  font-size: 11px;
  font-weight: 700;
  color: theme('colors.accent.600');
}

.cs-mark-portal b {
  font-weight: 600;
  color: theme('colors.secondary.400');
}

.cs-mark-tile {
  display: grid;
  place-items: center;
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  overflow: hidden;
  background: #fff;
  border: 1px solid theme('colors.secondary.200');
  border-radius: 8px;
}

.cs-mark-tile img {
  max-width: 30px;
  max-height: 30px;
  object-fit: contain;
}

/* Nav rows. Level-differentiated actives: top-level = lavender pill;
   parent = purple text when its group holds the active page; child = purple
   text only, no pill. */
.pn-row {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 11px;
  padding: 9px 12px;
  border-radius: 10px;
  font-size: 13.5px;
  font-weight: 700;
  color: theme('colors.secondary.900');
  white-space: nowrap;
  transition: background 0.12s, color 0.12s;
}
.pn-row:hover {
  background: #f6f6fa;
}
.pn-row-active,
.pn-row-active:hover {
  background: theme('colors.primary.100');
  color: theme('colors.primary.600');
}
.pn-ic {
  width: 20px;
  flex-shrink: 0;
  text-align: center;
  font-size: 17px;
  color: #9599b3;
}
.pn-row-active .pn-ic {
  color: theme('colors.primary.600');
}
.pn-parent:hover,
.pn-parent:hover .pn-ic,
.pn-parent-active,
.pn-parent-active .pn-ic {
  color: theme('colors.primary.600');
}
.pn-chev {
  margin-left: auto;
  font-size: 14px;
  color: #9599b3;
  transition: transform 0.15s;
}
.pn-chev-closed {
  transform: rotate(-180deg);
}

/* ═══ cs-tree — soft nav-tree connectors (sketch 066, v2) ═══
   The TRUNK is ONE element (.cs-tree::before): a single continuous stroke,
   thicker, which itself curves right at the bottom to reach the last child —
   seamless by construction, no joins. Each non-last child overlays a THINNER
   branch arc whose vertical part sits inside the trunk stroke (same x).
   Reliability constraint: rows are equal-height and no-wrap (--tree-row),
   which pins the trunk's end to the last child's center via bottom: row/2. */
.cs-tree {
  position: relative;
  --tree-x: 16px;
  --tree-w: 10px;
  --tree-r: 12px;
  --tree-trunk: 2.5px;
  --tree-branch: 1.5px;
  --tree-row: 38px;
  --tree-stroke: #e8eaf2;
  padding-left: var(--tree-x);
}
.cs-tree::before {
  content: '';
  position: absolute;
  left: 0;
  top: -4px;
  bottom: calc(var(--tree-row) / 2);
  width: var(--tree-w);
  border-left: var(--tree-trunk) solid var(--tree-stroke);
  border-bottom: var(--tree-trunk) solid var(--tree-stroke);
  border-bottom-left-radius: var(--tree-r);
}
.cs-tree > * {
  position: relative;
  height: var(--tree-row);
  white-space: nowrap;
}
.cs-tree > *:not(:last-child)::before {
  content: '';
  position: absolute;
  left: calc(-1 * var(--tree-x));
  top: calc(50% - 12px);
  height: 12px;
  width: 9px;
  border-left: var(--tree-branch) solid var(--tree-stroke);
  border-bottom: var(--tree-branch) solid var(--tree-stroke);
  border-bottom-left-radius: 10px;
}

.pn-kids {
  margin: 2px 0 8px 21px;
}
.pn-kid {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: theme('colors.secondary.500'); /* F-A11Y-2: #8f92a9 was 3.06:1 on white */
  transition: background 0.12s, color 0.12s;
}
.pn-kid:hover {
  color: theme('colors.secondary.900');
  background: #f6f6fa;
}
.pn-kid-active,
.pn-kid-active:hover {
  color: theme('colors.primary.600');
  background: none;
}
.pn-kid-ic {
  width: 18px;
  flex-shrink: 0;
  text-align: center;
  font-size: 16px;
  color: #a4a8bf;
}
.pn-kid:hover .pn-kid-ic,
.pn-kid-active .pn-kid-ic {
  color: inherit;
}

/* Account card — light treatment, gold avatar unchanged. */
.sb-account {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 8px 12px 10px;
  padding: 8px 10px;
  border-radius: 10px;
  transition: background 0.15s;
}
.sb-account:hover {
  background: theme('colors.secondary.50');
}
.sb-avatar {
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 12px;
  color: #fff;
  background: linear-gradient(135deg, var(--cs-gold), theme('colors.accent.700'));
}

/* Sidebar nav scrollbar — light. */
.portal-sidebar nav {
  scrollbar-width: thin;
  scrollbar-color: theme('colors.secondary.200') transparent;
}
.portal-sidebar nav::-webkit-scrollbar {
  width: 6px;
}
.portal-sidebar nav::-webkit-scrollbar-track {
  background: transparent;
}
.portal-sidebar nav::-webkit-scrollbar-thumb {
  background: theme('colors.secondary.200');
  border-radius: 3px;
}
.portal-sidebar nav::-webkit-scrollbar-thumb:hover {
  background: theme('colors.secondary.300');
}

/* Assigned-consultant card — soft-lavender inset (sketch 066 A). */
.consultant-card {
  margin: 10px 14px;
  padding: 12px 14px;
  background: theme('colors.primary.50');
  border: 1px solid theme('colors.primary.100');
  border-radius: 12px;
}
.consultant-eyebrow {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: theme('colors.secondary.600'); /* F-A11Y-2: #8b8fa7 was 2.89:1 on the card; 500 is 4.2:1 there */
}
.consultant-name {
  margin-top: 4px;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.2;
  color: theme('colors.secondary.900');
}
.consultant-mail {
  margin-top: 6px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: theme('colors.primary.600');
  transition: color 0.12s;
}
.consultant-mail:hover {
  color: theme('colors.primary.700');
}
.consultant-collapsed {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  color: #9599b3;
  transition: color 0.12s, background 0.12s;
}
.consultant-collapsed:hover {
  color: theme('colors.primary.600');
  background: theme('colors.secondary.50');
}

/* LIGHT header — white, sits above the light content. */
.portal-header {
  background: rgba(255, 255, 255, 0.92);
  border-color: theme('colors.secondary.200');
  backdrop-filter: blur(10px);
}

/* Staff view-as bar — faint gold tint to signal "staff context" on the light shell. */
.view-as-bar {
  background: rgba(201, 169, 98, 0.08);
  border-color: theme('colors.secondary.200');
}

/* Footer */
.portal-footer {
  border-color: theme('colors.secondary.200');
}

/* ── Messages entry point ──────────────────────────────────────────────────
   Purple leads on light (brand canon); the unread pill is the gold accent,
   which is the whole budget gold gets in this chrome. Ink on gold is
   purple-dark #2a1a5e, per tokens.md — never white, which fails on gold. */
.pl-msg {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 34px;
  padding: 0 0.7rem;
  border-radius: 8px; /* control tier */
  font-size: 13.5px;
  font-weight: 600;
  color: theme('colors.primary.700');
  transition: background 0.12s, color 0.12s;
}
.pl-msg:hover {
  background: theme('colors.primary.50');
  color: theme('colors.primary.600');
}
.pl-msg-active {
  background: theme('colors.primary.50');
}
.pl-msg-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px; /* pill tier */
  background: theme('colors.accent.500');
  color: #2a1a5e;
  font-size: 11px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}
/* The persistent "?" — a quiet square sibling of .pl-msg. Deliberately the
   lowest-contrast control in the header: it must be findable on every page
   without competing with Messages, which is the channel we want used. Square
   (34×34) at the control radius, so it reads as an icon button rather than a
   truncated pill. */
.pl-help {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  margin-left: 0.35rem;
  border-radius: 8px; /* control tier */
  color: theme('colors.secondary.500');
  transition: background 0.12s, color 0.12s;
}
.pl-help:hover {
  background: theme('colors.primary.50');
  color: theme('colors.primary.700');
}
</style>
